/**
 * TenantContext — May 2026
 *
 * Loaded once on app boot. Resolves the current tenant from the Host header
 * via the public /api/tenant/current endpoint and exposes:
 *   - tenant       → { id, slug, name, branding, features, ... }
 *   - hasFeature(k) → boolean (is this feature enabled for the current tenant?)
 *   - refresh()    → re-fetch (useful after super-admin toggles a flag)
 *
 * Also applies branding to the document (favicon, --primary-color CSS var)
 * so the app immediately re-skins when running on a tenant's subdomain.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from './lib/api';

const TenantContext = createContext({
  tenant: null,
  loading: true,
  hasFeature: () => true,  // fail-open so nothing breaks if context isn't loaded yet
  refresh: () => {},
});

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Super-admin impersonation: if they picked a tenant in the TopBar
      // switcher, that ID is in localStorage. Try that first; fall back to
      // host-based resolution if it 401s (e.g., not super-admin anymore).
      const impersonateId = (typeof window !== 'undefined')
        ? localStorage.getItem('crm_active_tenant_id')
        : null;
      let data = null;
      if (impersonateId) {
        try {
          const r = await api.get(`/tenant/by-id/${impersonateId}`);
          data = r.data;
        } catch {
          localStorage.removeItem('crm_active_tenant_id');
        }
      }
      if (!data) {
        const r = await api.get('/tenant/current');
        data = r.data;
      }
      setTenant(data);
      applyBranding(data);
    } catch {
      // Keep app working — default features open
      setTenant({ slug: 'default', name: 'Varo Trades CRM', features: {}, branding: {} });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const hasFeature = useCallback((key) => {
    if (!tenant) return true; // fail-open during boot
    const v = tenant.features?.[key];
    return v !== false;       // anything except explicit false is "on"
  }, [tenant]);

  return (
    <TenantContext.Provider value={{ tenant, loading, hasFeature, refresh }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() { return useContext(TenantContext); }

function applyBranding(t) {
  if (!t || !t.branding) return;
  try {
    const root = document.documentElement;
    if (t.branding.primary_color) {
      root.style.setProperty('--brand-primary', t.branding.primary_color);
    }
    if (t.branding.favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = t.branding.favicon_url;
    }
    if (t.branding.brand_name) {
      const baseTitle = document.title.split(' · ')[0];
      document.title = `${baseTitle} · ${t.branding.brand_name}`;
    }
  } catch { /* ignore */ }
}
